/**
 * Documents — link-only v1. Parents view documents from their workspaces.
 * Admins manage (add/remove) from this page when applicable.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import { FileText, ExternalLink, ArrowLeft, Plus, Trash2, X } from "lucide-react";

const KIND_LABEL = {
  form: "Form",
  consent: "Consent",
  immunization: "Immunization",
  calendar: "Calendar",
  policy: "Policy",
  newsletter: "Newsletter",
  other: "Document",
};

export default function Documents() {
  const { user } = useAuth();
  const { selected } = useChild();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    kind: "form",
    url: "",
  });

  const isAdmin = user?.role === "admin";
  const adminWsId = isAdmin ? selected?.workspace_id : null;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents/my", { params: { limit: 60 } });
      setDocs(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addDoc = async (e) => {
    e.preventDefault();
    if (!adminWsId) return;
    setAdding(true);
    try {
      await api.post(`/workspaces/${adminWsId}/documents`, form);
      setForm({ title: "", description: "", kind: "form", url: "" });
      await load();
      toast("Quietly added.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't add.");
    } finally {
      setAdding(false);
    }
  };

  const removeDoc = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      await load();
      toast("Removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <div data-testid="documents-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="documents-back"
          aria-label="Back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Back</span>
        </button>
        <Brand size="sm" dataTestId="brand-documents" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-2xl mx-auto fade-up">
        <span className="eyebrow">Documents</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          Forms, gently filed.
        </h1>
        <p className="mt-4 text-base text-[#6A6863] font-light max-w-md">
          Handbooks, consent forms, immunization records, the calendar PDF. Tap
          to open in a new tab.
        </p>

        <ol
          data-testid="documents-list"
          className="mt-10 space-y-3"
        >
          {loading && (
            <li className="font-serif italic text-[#6A6863]">A quiet moment…</li>
          )}
          {!loading && docs.length === 0 && (
            <li
              data-testid="documents-empty"
              className="font-serif italic text-[#6A6863] rounded-3xl border hairline bg-white p-7"
            >
              No documents yet. The center will share them when ready.
            </li>
          )}
          {docs.map((d) => (
            <li
              key={d.id}
              data-testid={`documents-row-${d.id}`}
              className="rounded-3xl border hairline bg-white p-6 flex items-start gap-4"
            >
              <span
                className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "#D4A85F" }}
                aria-hidden
              >
                <FileText size={16} strokeWidth={1.5} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="eyebrow">
                  {KIND_LABEL[d.kind] || "Document"} · {d.workspace_name || "Your center"}
                </div>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`documents-open-${d.id}`}
                  className="mt-2 font-serif text-lg text-[#2C2B29] hover:underline inline-flex items-center gap-2"
                >
                  {d.title}
                  <ExternalLink size={14} strokeWidth={1.5} />
                </a>
                {d.description && (
                  <p className="mt-2 text-sm text-[#6A6863] font-light">{d.description}</p>
                )}
                <p className="mt-2 text-xs text-[#6A6863]">added by {d.created_by_name}</p>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => removeDoc(d.id)}
                  data-testid={`documents-remove-${d.id}`}
                  aria-label="Remove"
                  className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              )}
            </li>
          ))}
        </ol>

        {isAdmin && adminWsId && (
          <form
            onSubmit={addDoc}
            data-testid="documents-add-form"
            className="mt-10 rounded-3xl border hairline bg-[#F2EFEA]/60 p-7 space-y-5"
          >
            <div className="flex items-center gap-2">
              <Plus size={14} strokeWidth={1.5} className="text-[#8A9984]" />
              <span className="eyebrow">Add a document link</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="eyebrow">Title</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Parent handbook"
                  data-testid="documents-add-title"
                  className="orbit-input mt-2"
                />
              </div>
              <div>
                <label className="eyebrow">Kind</label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  data-testid="documents-add-kind"
                  className="orbit-input mt-2 bg-transparent"
                >
                  {Object.entries(KIND_LABEL).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow">Link (https)</label>
                <input
                  type="url"
                  required
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                  data-testid="documents-add-url"
                  className="orbit-input mt-2"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow">Description (optional)</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="A short note for parents"
                  data-testid="documents-add-description"
                  className="orbit-input mt-2"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={adding || !form.title || !form.url}
                data-testid="documents-add-submit"
                className="btn-primary !py-2 !px-5 !text-xs"
              >
                {adding ? "Adding…" : "Add link"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
